import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { Editor } from '@tinymce/tinymce-react';
import { blogService } from '../../services/blogService';
import axios from '../../utils/axios';
import config from '../../config/config';
import './BlogEditor.css';

const LICENSE_KEY =
  'eyJhbGciOiJFUzI1NiJ9.eyJleHAiOjE3NjY3OTM1OTksImp0aSI6IjRlOGY0ODQ1LTIwMTItNGUxYS1hMzljLWRmOWZkM2MyNTc5MiIsImxpY2Vuc2VkSG9zdHMiOlsiMTI3LjAuMC4xIiwibG9jYWxob3N0IiwiMTkyLjE2OC4qLioiLCIxMC4qLiouKiIsIjE3Mi4qLiouKiIsIioudGVzdCIsIioubG9jYWxob3N0IiwiKi5sb2NhbCJdLCJ1c2FnZUVuZHBvaW50IjoiaHR0cHM6Ly9wcm94eS1ldmVudC5ja2VkaXRvci5jb20iLCJkaXN0cmlidXRpb25DaGFubmVsIjpbImNsb3VkIiwiZHJ1cGFsIl0sImxpY2Vuc2VUeXBlIjoiZGV2ZWxvcG1lbnQiLCJmZWF0dXJlcyI6WyJEUlVQIl0sInZjIjoiMjQ0NzRlYTcifQ.6JpqBG6x3Ah1KXczsUC26TyyiwfHmilTdHCTNv4fd5wL6gOnK_FyF82RhdnUUUterZKyXtXP5G3aNqw6JXLObg';

const BlogEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    author: '',
    tags: '',
    status: 'draft',
    featuredImage: null,
    featuredImagePreview: null
  });

  useEffect(() => {
    if (id) {
      fetchBlog();
    } else {
      setLoading(false);
    }
  }, [id]);

  const fetchBlog = async () => {
    try {
      const blog = await blogService.getBlog(id);
      setFormData({
        title: blog.title || '',
        content: blog.content || '',
        author: blog.author || '',
        tags: blog.tags ? blog.tags.join(', ') : '',
        status: blog.status || 'draft',
        featuredImage: null,
        featuredImagePreview: blog.featuredImage || null
      });
      setLoading(false);
    } catch (error) {
      console.error('Error fetching blog:', error);
      navigate('/admin/blogs');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      formDataToSend.append('content', formData.content);
      formDataToSend.append('author', formData.author);
      formDataToSend.append('tags', JSON.stringify(formData.tags.split(',').map(tag => tag.trim())));
      formDataToSend.append('status', formData.status);
      
      if (formData.featuredImage) {
        formDataToSend.append('image', formData.featuredImage);
      }

      if (id) {
        await blogService.updateBlog(id, formDataToSend);
      } else {
        await blogService.createBlog(formDataToSend);
      }

      navigate('/admin/blogs');
    } catch (error) {
      console.error('Error saving blog:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        featuredImage: file,
        featuredImagePreview: URL.createObjectURL(file)
      }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold text-white">
          {id ? 'Edit Blog Post' : 'Create New Blog Post'}
        </h1>
        <div className="flex space-x-4">
          <button
            onClick={() => navigate('/admin/blogs')}
            className="px-4 py-2 text-zinc-400 hover:text-white hover:bg-zinc-800/50 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
            disabled={isSubmitting}
          >
            {id ? 'Update Post' : 'Publish Post'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        <div className="col-span-2">
          <div className="bg-[#1a1a1a] rounded-xl border border-zinc-800 p-6 mb-6">
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Post title"
              className="w-full px-4 py-2 bg-zinc-800/50 border-none rounded-lg text-white placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-orange-500 text-xl mb-4"
              required
            />
            <Editor
              apiKey="3ulirlmwdm15i870wikt58hzsca401kcltjx3kpbo932r9s5"
              value={formData.content}
              onEditorChange={(content) => setFormData({ ...formData, content })}
              init={{
                height: 500,
                menubar: true,
                skin: 'oxide-dark',
                content_css: 'dark',
                plugins: [
                  'anchor', 'autolink', 'charmap', 'codesample', 'emoticons', 'image', 'link', 'lists', 
                  'media', 'searchreplace', 'table', 'visualblocks', 'wordcount',
                ],
                toolbar: 'undo redo | blocks fontfamily fontsize | bold italic underline strikethrough | ' +
                  'link image media table mergetags | addcomment showcomments | spellcheckdialog a11ycheck typography | ' +
                  'align lineheight | checklist numlist bullist indent outdent | emoticons charmap | removeformat',
                content_style: `
                  body {
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
                    font-size: 16px;
                    line-height: 1.6;
                    background: #1a1a1a;
                    color: #e8eaed;
                    margin: 1rem;
                  }
                  p { margin: 0 0 1rem 0; }
                  a { color: #f97316; }
                  a:hover { color: #fb923c; }
                  table { border-color: #4a4a4a; }
                  td, th { border-color: #4a4a4a; padding: 8px; }
                `,
                images_upload_url: '/api/blogs/upload-image',
                automatic_uploads: true,
                images_reuse_filename: true,
                images_upload_credentials: true,
                images_upload_handler: async function (blobInfo, progress) {
                  return new Promise((resolve, reject) => {
                    const formData = new FormData();
                    const file = blobInfo.blob();
                    formData.append('image', file, file.name || blobInfo.filename());

                    axios.post('/api/blogs/upload-image', formData, {
                      headers: {
                        'Content-Type': 'multipart/form-data',
                      },
                      onUploadProgress: (e) => {
                        progress(e.loaded / e.total * 100);
                      }
                    })
                    .then(response => {
                      resolve(response.data.location);
                    })
                    .catch(err => {
                      reject(err.response?.data?.message || 'Image upload failed');
                    });
                  });
                },
                setup: function(editor) {
                  editor.on('init', function() {
                    const container = editor.getContainer();
                    container.style.backgroundColor = '#1a1a1a';
                    container.style.border = '1px solid #4a4a4a';
                    container.style.borderRadius = '8px';
                    
                    const iframeElement = editor.iframeElement;
                    if (iframeElement) {
                      iframeElement.style.backgroundColor = '#1a1a1a';
                    }
                  });
                }
              }}
            />
          </div>
        </div>

        <div className="col-span-1">
          <div className="bg-[#1a1a1a] rounded-xl border border-zinc-800 p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Author</label>
              <input
                type="text"
                value={formData.author}
                onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                className="w-full px-4 py-2 bg-zinc-800/50 border-none rounded-lg text-white placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-orange-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Tags (comma separated)</label>
              <input
                type="text"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                className="w-full px-4 py-2 bg-zinc-800/50 border-none rounded-lg text-white placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-orange-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-4 py-2 bg-zinc-800/50 border-none rounded-lg text-white focus:outline-none focus:ring-1 focus:ring-orange-500"
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Featured Image</label>
              <div className="flex flex-col gap-4">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="block w-full text-sm text-zinc-400
                      file:mr-4 file:py-2 file:px-4
                      file:rounded-lg file:border-0
                      file:text-sm file:font-semibold
                      file:bg-orange-500 file:text-white
                      hover:file:bg-orange-600
                      file:cursor-pointer"
                />
                {formData.featuredImagePreview && (
                  <div className="relative">
                    <img
                      src={formData.featuredImagePreview}
                      alt="Featured image preview"
                      className="w-full h-40 object-cover rounded-lg"
                    />
                    <button
                      onClick={() => {
                        URL.revokeObjectURL(formData.featuredImagePreview);
                        setFormData(prev => ({
                          ...prev,
                          featuredImage: null,
                          featuredImagePreview: null
                        }));
                      }}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogEditor;
